import React, { Component } from 'react';
import { Container, Row, Col } from 'react-grid-system';

import { InfoTooltip } from 'components/tooltip';
import { I18n, translate } from 'components/translate';
import { isObject } from 'helpers/Object';
import { inject, observer } from 'mobx-react';
import { DeepSearchService, UserManagerService } from 'modules/userManager';
import { USER_PLUS_ACCESS } from 'modules/userManager/constants';
import {
  accessFilterFields,
  filters
} from 'modules/userManager/userSearch/deepSearch/constants';
import PropTypes from 'prop-types';

import AccessSelectDate from './AccessSelectDate';
import AccessService from './AccessService';
import PlusAccess from './PlusAccess';

import DeepSearchRow from '../commons/DeepSearchRow';
import DeepSearchSelect from '../commons/DeepSearchSelect';

const fields = {
  userPurchaseDate: 'userPurchaseDate',
  userFirstAccess: 'userFirstAccess',
  userLastAccess: 'userLastAccess',
  userStatus: 'userStatus',
  accessPlusAll: 'accessPlusAll',
  userPlusAccess: 'userPlusAccess'
};

const filterCount = 'accessFilterCount';

const options = {
  userStatusList: 'userStatusList',
  accessPlusAllList: 'accessPlusAllList',
  userPlusAccessList: 'userPlusAccessList'
};

const clearFunctionKey = 'deepSearchAccess';

const initialState = {
  userPurchaseDate: {
    open: false,
    beginDate: '',
    endDate: '',
    value: ''
  },
  userFirstAccess: {
    open: false,
    beginDate: '',
    endDate: '',
    value: ''
  },
  userLastAccess: {
    open: false,
    beginDate: '',
    endDate: '',
    value: ''
  },
  userStatus: {
    value: '',
    open: false
  },
  accessPlusAll: {
    value: '',
    open: false
  },
  userPlusAccess: {
    value: '',
    open: false
  }
};

const accessPlusYes = 'YES';

@translate()
@inject((stores) => ({
  accessFilter: stores.DeepSearchStore.deepSearch.accessFilter
}))
@observer
export default class DeepSearchAccess extends Component {
  static propTypes = {
    accessFilter: PropTypes.object,
    setClearFunction: PropTypes.func,
    t: PropTypes.func,
    unsetClearFunction: PropTypes.func
  };

  constructor(props) {
    super(props);

    const { accessFilter } = props;
    const userPlusAccessValue =
      accessFilter.userPlusAccess.option || accessFilter.userPlusAccess.value;
    const accessPlusAllValue = this.accessPlusAllValue(
      userPlusAccessValue,
      accessFilter.accessPlusAll
    );
    const selectDateValue = (f) => ({
      value: f.dateFilterType,
      from: f.from,
      until: f.until
    });

    this.state = {
      userPurchaseDate: {
        ...initialState.userPurchaseDate,
        value:
          accessFilter.userPurchaseDate.option ||
          selectDateValue(accessFilter.userPurchaseDate)
      },
      userFirstAccess: {
        ...initialState.userFirstAccess,
        value:
          accessFilter.userFirstAccess.option ||
          selectDateValue(accessFilter.userFirstAccess)
      },
      userLastAccess: {
        ...initialState.userLastAccess,
        value:
          accessFilter.userLastAccess.option ||
          selectDateValue(accessFilter.userLastAccess)
      },
      userStatus: {
        ...initialState.userStatus,
        value: accessFilter.userStatus.option || accessFilter.userStatus.query
      },
      accessPlusAll: {
        ...initialState.accessPlusAll,
        value: accessPlusAllValue
      },
      userPlusAccess: {
        ...initialState.userPlusAccess,
        value: userPlusAccessValue
      }
    };
  }

  accessPlusAllValue(userPlusAccessValue, accessPlusAll) {
    let accessPlusAllValue = accessPlusAll.option;

    if (userPlusAccessValue) {
      accessPlusAllValue = UserManagerService.hasPlusAccess(userPlusAccessValue)
        ? accessPlusYes
        : USER_PLUS_ACCESS.WITHOUT_PLUS_ACCESS;
    }

    DeepSearchService.setFilter(
      DeepSearchService.deepSearchObject,
      filters.accessFilter,
      accessFilterFields.accessPlusAll,
      { query: accessPlusAllValue, value: accessPlusAllValue },
      false
    );

    return accessPlusAllValue;
  }

  clearFunctions = [];

  componentDidMount() {
    const { setClearFunction } = this.props;

    setClearFunction && setClearFunction(clearFunctionKey, this.clearState);
  }

  componentWillUnmount() {
    const { unsetClearFunction } = this.props;

    unsetClearFunction && unsetClearFunction(clearFunctionKey);
  }

  clearState = () => {
    this.setState({ ...initialState });
    this.clearFunctions.forEach((clearFunction) => clearFunction());
  };

  setClearFunctionReference = (clearFunction) => {
    this.clearFunctions.push(clearFunction);
  };

  onMenuOpen = (field, state) => {
    this.setState((prevState) => ({
      [field]: {
        ...prevState[field],
        open: state
      }
    }));
  };

  onOpenSelectDate = (field) => {
    this.setState((prevState) => ({
      [field]: {
        ...prevState[field],
        open: !this.state[field].open
      }
    }));
  };

  cleanData = (field) => {
    const emptyValue = '';
    const fieldData = this.props.accessFilter[field].option || {};

    DeepSearchService.updateFilterCount(
      emptyValue,
      fieldData.value,
      filterCount
    );
    AccessService.updateDateFieldData(
      field,
      emptyValue,
      emptyValue,
      emptyValue,
      emptyValue
    );

    this.setState((prevState) => ({
      [field]: {
        ...prevState[field],
        beginDate: '',
        endDate: '',
        value: ''
      }
    }));
  };

  cleanAccessPlus = (field) => {
    this.cleanData(field);
    this.cleanAcessPlusType();
  };

  setValue = (field, value, beginDate, endDate, increment = true) => {
    const fieldData = this.props.accessFilter[field].option || {};

    increment &&
      DeepSearchService.updateFilterCount(value, fieldData.value, filterCount);
    AccessService.updateDateFieldData(
      field,
      value,
      value.name,
      beginDate,
      endDate
    );

    this.setState((prevState) => ({
      [field]: {
        ...prevState[field],
        value,
        open: false,
        beginDate,
        endDate
      }
    }));
  };

  cleanAcessPlusType() {
    this.setValueSelect(null, accessFilterFields.userPlusAccess);
  }

  setValueSelectedAccessPlus = (option, field) => {
    const isAccessPlus = field === accessFilterFields.accessPlusAll;
    const needType = option && option.value === accessPlusYes;
    const shouldClearUserPlusAccess = isAccessPlus && !needType;

    shouldClearUserPlusAccess && this.cleanAcessPlusType();
    this.setValueSelect(option, field);
  };

  setValueSelect = (option, field) => {
    AccessService.updateSelectFieldsStore(option, field, accessFilterFields);

    DeepSearchService.setDirty(true);

    this.setState((prevState) => ({
      [field]: {
        ...prevState[field],
        value: option,
        open: false
      }
    }));
  };

  setFieldValue = (field, value) => {
    this.setState((prevState) => ({
      [field]: {
        ...prevState[field],
        value
      }
    }));
  };

  get isAccessPlusTypeUsersEnabled() {
    const { accessPlusAll } = this.state;
    const accessPlusAllValue = isObject(accessPlusAll.value)
      ? accessPlusAll.value.value
      : accessPlusAll.value;

    return accessPlusAllValue === accessPlusYes;
  }

  render() {
    return (
      <Container className="deep__search__inner-container" fluid>
        <Row nogutter>
          <Col xs={12} md={3} className="deep__search__tab-title">
            <I18n label="users_manager.deep_search.lateral_menu.access" />
            <InfoTooltip
              rerenderPosition={true}
              text="users_manager.deep_search.access.tooltip"
            />
          </Col>
          <Col xs={12} md={9} />
        </Row>

        <DeepSearchRow label="general.purchase_date">
          <AccessSelectDate
            field={accessFilterFields.userPurchaseDate}
            fieldData={this.state.userPurchaseDate}
            cleanData={this.cleanData}
            onMenuOpen={this.onOpenSelectDate}
            setValue={this.setValue}
            setClearFunction={this.setClearFunctionReference}
          />
        </DeepSearchRow>

        <DeepSearchRow label="general.first_access">
          <AccessSelectDate
            field={accessFilterFields.userFirstAccess}
            fieldData={this.state.userFirstAccess}
            cleanData={this.cleanData}
            onMenuOpen={this.onOpenSelectDate}
            setValue={this.setValue}
            setClearFunction={this.setClearFunctionReference}
          />
        </DeepSearchRow>

        <DeepSearchRow label="general.last_access">
          <AccessSelectDate
            field={accessFilterFields.userLastAccess}
            fieldData={this.state.userLastAccess}
            cleanData={this.cleanData}
            onMenuOpen={this.onOpenSelectDate}
            setValue={this.setValue}
            setClearFunction={this.setClearFunctionReference}
          />
        </DeepSearchRow>

        <DeepSearchRow label="users_manager.deep_search.lateral_menu.access">
          <DeepSearchSelect
            name="userStatus"
            field={accessFilterFields.userStatus}
            placeholder="users_manager.deep_search.access.access_placeholder"
            onMenuOpen={this.onMenuOpen}
            options={options.userStatusList}
            setValueSelect={this.setValueSelect}
            value={this.state.userStatus.value}
            isOpen={this.state.userStatus.open}
          />
        </DeepSearchRow>

        <DeepSearchRow label="sales_manager.access_plus">
          <PlusAccess
            fields={fields}
            onMenuOpen={this.onMenuOpen}
            options={options}
            setValueSelect={this.setValueSelect.bind(this)}
            accessPlusAllValue={this.state.accessPlusAll.value}
            userPlusAccessValue={this.state.userPlusAccess.value}
            setFieldValuePlusAll={this.setFieldValue.bind(this)}
            isAccessPlusAllOpen={this.state.accessPlusAll.open}
            isUserPlusAccessOpen={this.state.userPlusAccess.open}
            isAccessPlusUserDisabled={!this.isAccessPlusTypeUsersEnabled}
          />
        </DeepSearchRow>
      </Container>
    );
  }
}
